-- | Commands, updates, and transition logic.
-- | In particular: any of these that affect application-level state.
module App.Update
    ( Command(..)
    , UpdateResult
    , bootstrap
    , update
    ) where

import Common

import API.Account (AccountType(..))
import API.Autofill (AutofillArgs)
import API.Document (CopyDocRequest)
import API.RecentForms (RecentForm)
import API.Signature (SignatureRequest)
import API.Types (DocumentID)
import App.Msg (Msg(..))
import App.Operation.Store as OpStore
import App.Operation.Types (OperationId, OperationKind(..), OperationScope(..))
import App.Route as AppRoute
import App.State (AppState, RouteHistoryMode(..), Step1Session(..), Step2ASession(..), Step2BSession(..), Step3Init, Step3Session(..), activateStep)
import App.Step (Navigation(..), Step(..))
import Data.Array as Arr
import Data.Map as Map
import Data.Maybe (isJust)
import Error (ErrorContext(..), ErrorWrapper)
import Form.StudentSelect as StudentSelect
import Internal.SelectionMap as Sel
import Routing.PushState (LocationState)
import Steps.FormPicker as FormPicker
import Steps.Step1.State (InitialState, RecentFormState, RecentFormStateFlag(..), applyRecentFormsPage, applySearchChanged, finishLoading, selectedDocumentIds, selectedForms, setError, startLoading) as Step1
import Steps.Step1.Update (DocExport, Msg(..), RecentFormAction(..), UpdateFormsSelection, applyFormsUpdateSelection, applyToggleLockResults, copyRequests, findExportReport, toggleLockRequests) as Step1
import Steps.Step3.State as Step3State
import Steps.Step3.Transition (PendingTransition, Step3HistoryMode(..))
import Steps.Step3.Types as Step3Types
import Steps.Step3.Update as Step3Update
import Steps.WorkQueue.Cache (WorkQueueCache, getDocByStudent)
import Steps.WorkQueue.DocumentIndex (listDocs)
import Steps.WorkQueue.State (FormSectionID, WorkQueueState, editDocsWorkQueue, formForSelected, resetWorkQueueState, setCurrentDoc)
import Steps.WorkQueue.UIState (WorkQueueUiState, resetWorkQueueUiState)
import Steps.WorkQueue.View as WQ

data Command
    = SyncRoute RouteHistoryMode
    | HydrateLocation Int LocationState
    | RunStep1InitialLoad OperationId Step1.InitialState
    | DebounceStep1Search Int
    | RunStep1Search OperationId Step1.RecentFormState
    | RunStep1PageChange OperationId Step1.RecentFormState Int
    | RunStep1CurrentPageReload OperationId Step1.RecentFormState
    | RunStep1SaveFormOrder OperationId Step1.RecentFormState
    | RunStep1ToggleLocks OperationId Step1.RecentFormState
    | RunStep1CopyDocuments OperationId Step1.RecentFormState (Array CopyDocRequest)
    | RunStep1DeleteDocuments OperationId Step1.RecentFormState
    | RunStep1ExportDocuments OperationId Step1.RecentFormState Step1.DocExport
    | RunStep1RequestSignatures OperationId (Array SignatureRequest)
    | RunStep1UpdateForms OperationId Step1.RecentFormState Step1.UpdateFormsSelection
    | RunStep2AInitialLoad OperationId WorkQueueState WorkQueueCache
    | RunStep2BLoad OperationId WorkQueueState WorkQueueUiState WorkQueueCache
    | RunStep2ExportAll OperationId WorkQueueState WorkQueueCache
    | RunStep3FormTypesLoad OperationId Step3Init
    | RunStep3InitialLoad OperationId OperationKind Step3Init
    | SyncStep3Route Step3HistoryMode
    | ScrollStep3Section FormSectionID
    | SaveStep3 OperationId OperationKind (Maybe Int)
    | LoadStep3 OperationId OperationKind PendingTransition
    | AutofillStep3 OperationId OperationKind AutofillArgs
    | ToggleStep3Lock OperationId OperationKind
    | RunStep3DocumentMutation OperationId OperationKind Step3Types.DocumentMutationRequest
    | ConfirmStep3Cancel
    | AllowPageUnload
    | BlockPageUnload
    | OpenExportUrl String
    | OpenExternalUrl String
    | ReportError ErrorWrapper

type UpdateResult =
    { state :: AppState
    , commands :: Array Command
    }

bootstrap :: AppState -> UpdateResult
bootstrap state = scheduleBootstrapOps { state, commands: [] }

update :: AppState -> Msg -> Either ErrorWrapper UpdateResult
update state msg = map scheduleBootstrapOps $ case msg of
    BrowserLocationChanged locationState ->
        let requested = AppRoute.parseRoute locationState
            expected = currentRouteForState state locationState
            nextState = advanceRouteVersion state
            routeVersion = nextState.routeVersion
        in do
            handled <- handleReadyStep3BrowserRoute nextState requested
            case handled of
                Just result ->
                    pure result
                Nothing | AppRoute.routeUrl requested == AppRoute.routeUrl expected ->
                    pure { state: nextState, commands: [] }
                Nothing ->
                    pure
                        { state: nextState
                        , commands: [ HydrateLocation routeVersion locationState ]
                        }

    HydratedLocation routeVersion locationState result ->
        if routeVersion /= state.routeVersion
            then pure { state, commands: [] }
            else do
                nextState <- result
                let requested = AppRoute.parseRoute locationState
                let canonical = nextState.route
                let routeCommands = if AppRoute.routeUrl requested == AppRoute.routeUrl canonical
                        then []
                        else [ SyncRoute ReplaceRouteHistory ]
                pure
                    { state: nextState
                    , commands: routeCommands
                    }

    Step1Msg step1Msg -> case state.step of
        Step1 -> case state.step1 of
            Step1Ready ready ->
                pure $ applyStep1Msg ready step1Msg { state, commands: [] }
            _ ->
                pure { state, commands: [] }
        _ ->
            pure { state, commands: [] }

    Step1SearchDebounced version -> case state.step of
        Step1 -> case state.step1 of
            Step1Ready ready | ready.searchVersion == version ->
                pure $ startStep1Operation
                    LoadRecentForms
                    (\opId nextReady -> RunStep1Search opId nextReady)
                    identity
                    { state, commands: [] }
            _ ->
                pure { state, commands: [] }
        _ ->
            pure { state, commands: [] }

    Step1InitialLoaded opId result -> case state.step1 of
        Step1Loading { opId: Just currentOpId } | currentOpId == opId -> do
            ready <- result
            pure
                { state: setStep1Ready ready $ state { preselectForms = [] }
                , commands: []
                }
        _ ->
            pure { state, commands: [] }

    Step1RecentFormsLoaded opId result
        | not (isCurrentOperation Step1RecentFormsScope opId state) ->
            pure { state, commands: [] }
        | otherwise -> case state.step1 of
            Step1Ready ready -> case result of
                Right page ->
                    pure
                        { state: setStep1Ready (Step1.applyRecentFormsPage page ready) state
                        , commands: []
                        }
                Left err ->
                    pure $ step1Error err state
            _ ->
                pure { state, commands: [] }

    Step1FormOrderSaved opId result
        | not (isLatestOperation Step1FormOrderScope opId state) ->
            pure { state, commands: [] }
        | otherwise -> case state.step1 of
            Step1Ready ready -> case result of
                Right formPicker ->
                    pure
                        { state: setStep1Ready (Step1.finishLoading (ready { formPicker = formPicker })) state
                        , commands: []
                        }
                Left err ->
                    pure $ step1Error err state
            _ ->
                pure { state, commands: [] }

    Step1LocksToggled opId result
        | not (isLatestOperation Step1DocumentMutationScope opId state) ->
            pure { state, commands: [] }
        | otherwise -> case state.step1 of
            Step1Ready ready -> case result of
                Right lockResults ->
                    pure
                        { state: setStep1Ready (Step1.finishLoading $ Step1.applyToggleLockResults lockResults ready) state
                        , commands: []
                        }
                Left err ->
                    pure $ step1Error err state
            _ ->
                pure { state, commands: [] }

    Step1DocumentsCopied opId result
        | not (isLatestOperation Step1DocumentMutationScope opId state) ->
            pure { state, commands: [] }
        | otherwise -> case state.step1 of
            Step1Ready _ -> case result of
                Right _ ->
                    pure $ startStep1Operation
                        LoadRecentForms
                        (\nextOpId nextReady -> RunStep1PageChange nextOpId nextReady 0)
                        (\st -> st { selection = Sel.empty })
                        { state, commands: [] }
                Left err ->
                    pure $ step1Error err state
            _ ->
                pure { state, commands: [] }

    Step1DocumentsDeleted opId result
        | not (isLatestOperation Step1DocumentMutationScope opId state) ->
            pure { state, commands: [] }
        | otherwise -> case state.step1 of
            Step1Ready _ -> case result of
                Right _ ->
                    pure $ startStep1Operation
                        LoadRecentForms
                        (\nextOpId nextReady -> RunStep1Search nextOpId nextReady)
                        (\st -> st { selection = Sel.empty })
                        { state, commands: [] }
                Left err ->
                    pure $ step1Error err state
            _ ->
                pure { state, commands: [] }

    Step1DocumentsExported opId etype result
        | not (isLatestOperation Step1ExportScope opId state) ->
            pure { state, commands: [] }
        | otherwise -> case state.step1 of
            Step1Ready ready -> case result of
                Right reports -> case Step1.findExportReport etype reports of
                    Just report ->
                        pure
                            { state: setStep1Ready (Step1.finishLoading ready) state
                            , commands: [ OpenExportUrl report.url ]
                            }
                    Nothing ->
                        pure $ step1Error
                            (WithContext (InModule "Steps.Step1") $
                                WithContext (DuringAction "exporting documents") $
                                    StringError "Expected at least one report for exported documents.")
                            state
                Left err ->
                    pure $ step1Error err state
            _ ->
                pure { state, commands: [] }

    Step1SignaturesRequested opId result
        | not (isLatestOperation Step1DocumentMutationScope opId state) ->
            pure { state, commands: [] }
        | otherwise -> case state.step1 of
            Step1Ready _ -> case result of
                Right _ ->
                    pure $ startStep1Operation
                        LoadRecentForms
                        (\nextOpId nextReady -> RunStep1CurrentPageReload nextOpId nextReady)
                        (\st -> st { selection = Sel.empty })
                        { state, commands: [] }
                Left err ->
                    pure $ step1Error err state
            _ ->
                pure { state, commands: [] }

    Step1FormsUpdated opId selection result
        | not (isLatestOperation Step1DocumentMutationScope opId state) ->
            pure { state, commands: [] }
        | otherwise -> case state.step1 of
            Step1Ready ready -> case result of
                Right _ ->
                    pure $ startStep1Operation
                        LoadRecentForms
                        (\nextOpId nextReady -> RunStep1Search nextOpId nextReady)
                        (\st -> Step1.applyFormsUpdateSelection selection (st { selection = Sel.empty }))
                        { state: setStep1Ready ready state
                        , commands: []
                        }
                Left err ->
                    pure $ step1Error err state
            _ ->
                pure { state, commands: [] }

    Step2AAction action -> case state.step, state.step2A of
        Step2A, Step2AReady ready ->
            pure $ applyStep2AAction ready action { state, commands: [] }
        _, _ ->
            pure { state, commands: [] }

    Step2AInitialLoaded opId result -> case state.step2A of
        Step2ALoading { opId: Just currentOpId } | currentOpId == opId -> case result of
            Right ready ->
                pure
                    { state: state
                        { step2A = Step2AReady ready
                        , workQueueState = state.workQueueState { students = ready.students }
                        }
                    , commands: []
                    }
            Left err ->
                pure
                    { state: state { step2A = Step2ALoadFailed err }
                    , commands: []
                    }
        _ ->
            pure { state, commands: [] }

    Step2BAction action -> case state.step, state.step2B of
        Step2B, Step2BReady ready ->
            pure $ applyStep2BAction ready action { state, commands: [] }
        _, _ ->
            pure { state, commands: [] }

    Step2BLoaded opId result -> case state.step2B of
        Step2BLoading { opId: Just currentOpId } | currentOpId == opId -> case result of
            Right ready ->
                pure
                    { state: syncStep2BState state ready
                    , commands: []
                    }
            Left err ->
                pure
                    { state: state { step2B = Step2BLoadFailed err }
                    , commands: []
                    }
        _ ->
            pure { state, commands: [] }

    Step2FormsExported opId result
        | not (isLatestOperation Step2ExportScope opId state) ->
            pure { state, commands: [] }
        | otherwise -> case result of
            Right reports -> case Arr.head reports of
                Just report ->
                    let exportResult = { state, commands: [ OpenExportUrl report.url ] }
                    -- Browser navigation can leave Step 2 while the export request is still
                    -- pending. Only the current Step 2 screens should use NavCancel to return
                    -- home after opening the generated report.
                    in pure $ if isCurrentStep2 state.step
                        then applyStep2Navigation NavCancel state exportResult
                        else exportResult

                Nothing ->
                    let err = missingStep2ExportReportError
                    in pure $ step2ExportError err state
            Left err ->
                pure $ step2ExportError err state

    Step3FormTypesLoaded opId result -> case state.step3 of
        Step3LoadingFormTypes { init, opId: Just currentOpId }
            | currentOpId == opId -> do
            preload <- result
            let nextState = state
                    { workQueueState = preload.state
                    , workQueueCache = preload.cache
                    , step3 = Step3LoadingInitialState { init, opId: Nothing }
                    }
            pure { state: nextState, commands: [] }
        _ ->
            pure { state, commands: [] }

    Step3InitialLoaded opId result -> case state.step3 of
        Step3LoadingInitialState { opId: Just currentOpId }
            | currentOpId == opId -> do
            ready <- result
            pure $ appendRouteCommand (SyncStep3Route ReplaceStep3History)
                { state: syncReadyState state ready
                , commands: []
                }
        _ ->
            pure { state, commands: [] }

    Step3Msg step3Msg -> case state.step3 of
        Step3Ready ready -> do
            step3Result <- Step3Update.update ready step3Msg
            pure $ applyStep3Commands
                { state: syncReadyState state step3Result.state
                , commands: []
                }
                step3Result.commands
        _ ->
            pure { state, commands: [] }

    Step3DocumentRequestHandled ->
        pure { state, commands: [] }

    OpStarted opId kind generation ->
        pure
            { state: state { operations = OpStore.recordStartedOperation opId kind generation state.operations }
            , commands: []
            }

    OpCompleted opId ->
        pure
            { state: state { operations = OpStore.completeOperation opId (Right unit) state.operations }
            , commands: []
            }

    OpFailed opId err ->
        pure
            { state: state { operations = OpStore.completeOperation opId (Left err) state.operations }
            , commands: []
            }

    OpSuperseded opId ->
        pure
            { state: state { operations = OpStore.markSuperseded opId state.operations }
            , commands: []
            }

scheduleBootstrapOps :: UpdateResult -> UpdateResult
scheduleBootstrapOps result = case result.state.step of
    Step1 | hasHydrateCommand result.commands ->
        result

    Step1 -> case result.state.step1 of
        Step1Loading { init, opId: Nothing } ->
            let nextState = applyStep1OperationStartPolicy LoadRecentForms result.state
                Tuple opId operations = OpStore.reserveOperationId nextState.operations
            in result
                { state = nextState
                    { operations = operations
                    , step1 = Step1Loading { init, opId: Just opId }
                    }
                , commands = result.commands <> [ RunStep1InitialLoad opId init ]
                }

        _ ->
            scheduleStep2BootstrapOps result

    _ ->
        scheduleStep2BootstrapOps result

scheduleStep2BootstrapOps :: UpdateResult -> UpdateResult
scheduleStep2BootstrapOps result = case result.state.step of
    Step2A | hasHydrateCommand result.commands ->
        result

    Step2A -> case result.state.step2A of
        Step2ALoading { workQueueState, workQueueCache, opId: Nothing } ->
            let nextState = applyStep2OperationStartPolicy LoadStep2Students result.state
                Tuple opId operations = OpStore.reserveOperationId nextState.operations
            in result
                { state = nextState
                    { operations = operations
                    , step2A = Step2ALoading { workQueueState, workQueueCache, opId: Just opId }
                    }
                , commands = result.commands <> [ RunStep2AInitialLoad opId workQueueState workQueueCache ]
                }

        _ ->
            result

    Step2B | hasHydrateCommand result.commands ->
        result

    Step2B -> case result.state.step2B of
        Step2BLoading { workQueueState, workQueueUi, workQueueCache, opId: Nothing } ->
            let nextState = applyStep2OperationStartPolicy LoadStep2FormPicker result.state
                Tuple opId operations = OpStore.reserveOperationId nextState.operations
            in result
                { state = nextState
                    { operations = operations
                    , step2B = Step2BLoading { workQueueState, workQueueUi, workQueueCache, opId: Just opId }
                    }
                , commands = result.commands <> [ RunStep2BLoad opId workQueueState workQueueUi workQueueCache ]
                }

        _ ->
            result

    _ ->
        scheduleStep3BootstrapOps result

scheduleStep3BootstrapOps :: UpdateResult -> UpdateResult
scheduleStep3BootstrapOps result = case result.state.step3 of
    Step3LoadingFormTypes { init, opId: Nothing } ->
        let Tuple opId operations = OpStore.reserveOperationId result.state.operations
        in result
            { state = result.state
                { operations = operations
                , step3 = Step3LoadingFormTypes { init, opId: Just opId }
                }
            , commands = result.commands <> [ RunStep3FormTypesLoad opId init ]
            }

    Step3LoadingInitialState { init, opId: Nothing } ->
        let kind = Step3State.initialLoadOperationKind result.state.workQueueState init.doc
            Tuple opId operations = OpStore.reserveOperationId result.state.operations
        in result
            { state = result.state
                { operations = operations
                , step3 = Step3LoadingInitialState { init, opId: Just opId }
                }
            , commands = result.commands <> [ RunStep3InitialLoad opId kind init ]
            }

    _ ->
        result

hasHydrateCommand :: Array Command -> Boolean
hasHydrateCommand = Arr.any isHydrate
  where
    isHydrate = case _ of
        HydrateLocation _ _ -> true
        _ -> false

-- Step 2 views still render their own controls, but app update owns the state
-- changes that affect navigation, shared work-queue data, and async exports.
applyStep2AAction :: StudentSelect.SelectStudentState -> StudentSelect.SelectStudentStepAction -> UpdateResult -> UpdateResult
applyStep2AAction ready action result = case action of
    StudentSelect.StateChange nextReady ->
        let nextState = result.state
                { workQueueState = result.state.workQueueState { students = nextReady.students }
                , step2A = Step2AReady nextReady
                }
        in result { state = nextState }

    StudentSelect.WorkQA (WQ.UIStateChange nextUi) ->
        result { state = result.state { workQueueUi = nextUi } }

    StudentSelect.WorkQA _ ->
        result

    StudentSelect.QueueChanged nextWorkQueue nextUi ->
        result { state = result.state { workQueueState = nextWorkQueue, workQueueUi = nextUi } }

    StudentSelect.Done | formForSelected result.state.workQueueCache.account result.state.workQueueState ->
        applyStep2Navigation NavNext result.state result

    StudentSelect.Done ->
        result { state = result.state { step2A = Step2AReady $ ready { msg = Just $ lookupTranslation "must-select-teacher-semester" } } }

    StudentSelect.Cancel ->
        applyStep2Navigation NavCancel result.state result

    StudentSelect.Export | formForSelected result.state.workQueueCache.account result.state.workQueueState ->
        startStep2ExportAll result

    StudentSelect.Export ->
        result { state = result.state { step2A = Step2AReady $ ready { msg = Just $ lookupTranslation "must-select-teacher-semester" } } }

applyStep2BAction :: FormPicker.FormPickerState -> FormPicker.FormPickerAction -> UpdateResult -> UpdateResult
applyStep2BAction ready action result = case action of
    FormPicker.State nextReady ->
        result { state = syncStep2BState result.state nextReady }

    FormPicker.WorkQR (WQ.UIStateChange nextUi) ->
        result { state = syncStep2BState result.state (ready { workQueueUi = nextUi }) }

    FormPicker.WorkQR _ ->
        result

    FormPicker.Navigate NavNext | not $ formForSelected ready.workQueueCache.account ready.workQueueState ->
        result

    FormPicker.Navigate nav ->
        applyStep2Navigation nav (syncStep2BState result.state ready) result

    FormPicker.Export | formForSelected ready.workQueueCache.account ready.workQueueState ->
        startStep2ExportAll $ result { state = syncStep2BState result.state ready }

    FormPicker.Export ->
        result

syncStep2BState :: AppState -> FormPicker.FormPickerState -> AppState
syncStep2BState state ready =
    state
        { step2B = Step2BReady ready
        , workQueueState = ready.workQueueState
        , workQueueUi = ready.workQueueUi
        , workQueueCache = ready.workQueueCache
        }

applyStep2Navigation :: Navigation -> AppState -> UpdateResult -> UpdateResult
applyStep2Navigation nav state result =
    let transition = applyRuntimeNavigation nav state
        nextState = if transition.shouldSyncRoute
            then advanceRouteVersion transition.state
            else transition.state
    in
        { state: nextState
        , commands: if transition.shouldSyncRoute then result.commands <> [ SyncRoute PushRouteHistory ] else result.commands
        }

isCurrentStep2 :: Step -> Boolean
isCurrentStep2 = case _ of
    Step2A -> true
    Step2B -> true
    _ -> false

startStep2ExportAll :: UpdateResult -> UpdateResult
startStep2ExportAll result
    | OpStore.isScopeActive result.state.operations Step2ExportScope =
        result
    | otherwise =
        let nextState = applyStep2OperationStartPolicy ExportStep2Forms result.state
            Tuple opId operations = OpStore.startOperation ExportStep2Forms Nothing nextState.operations
        in result
            { state = nextState { operations = operations }
            , commands = result.commands <> [ RunStep2ExportAll opId nextState.workQueueState nextState.workQueueCache ]
            }

applyStep2OperationStartPolicy :: OperationKind -> AppState -> AppState
applyStep2OperationStartPolicy kind state =
    Arr.foldl (\acc scope -> supersedeLatestActiveInScope scope acc) state $ supersededScopesForStep2Operation kind

supersededScopesForStep2Operation :: OperationKind -> Array OperationScope
supersededScopesForStep2Operation = case _ of
    LoadStep2Students -> [ Step2StudentSelectScope ]
    LoadStep2FormPicker -> [ Step2FormPickerScope ]
    _ -> []

missingStep2ExportReportError :: ErrorWrapper
missingStep2ExportReportError =
    WithContext (InModule "App.Update") $
        WithContext (DuringAction "exporting selected forms") $
            StringError "Expected at least one report for exported forms."

step2ExportError :: ErrorWrapper -> AppState -> UpdateResult
step2ExportError err state = case state.step of
    -- Keep the current Step 2 route visible on a failure page. The page has a
    -- reload action, so the user can recover and retry the export.
    Step2A ->
        { state: state { step2A = Step2ALoadFailed err }
        , commands: []
        }
    Step2B ->
        { state: state { step2B = Step2BLoadFailed err }
        , commands: []
        }
    _ ->
        { state
        , commands: [ ReportError err ]
        }

applyStep1Msg :: Step1.RecentFormState -> Step1.Msg -> UpdateResult -> UpdateResult
applyStep1Msg ready0 msg result0
    | shouldBlockStep1Message ready0 msg result0.state =
        result0
    | otherwise =
        let
            accepted = acceptStep1Message ready0 msg result0
            ready = accepted.ready
            result = accepted.result
        in case msg of
    Step1.StateChanged nextReady ->
        result { state = setStep1Ready (nextReady { searchVersion = ready.searchVersion }) result.state }

    Step1.ViewAction (Step1.SearchChanged searchState) ->
        let nextReady = Step1.applySearchChanged searchState ready
            nextState = supersedeLatestActiveInScope Step1RecentFormsScope result.state
        in
            { state: setStep1Ready nextReady nextState
            , commands: result.commands <> [ DebounceStep1Search nextReady.searchVersion ]
            }

    Step1.ViewAction (Step1.FormPickerChanged formPicker) | formPicker.reorder ->
        result { state = setStep1Ready (ready { formPicker = formPicker }) result.state }

    Step1.ViewAction (Step1.FormPickerChanged formPicker) ->
        startStep1Operation
            LoadRecentForms
            (\opId nextReady -> RunStep1Search opId nextReady)
            (\st -> st { formPicker = formPicker, selection = Sel.empty })
            result

    Step1.ViewAction Step1.StartFormReorder ->
        result { state = setStep1Ready (ready { formPicker = FormPicker.setFormReorder ready.formPicker true }) result.state }

    Step1.ViewAction Step1.SaveFormOrder ->
        startStep1Operation
            SaveStep1FormOrder
            (\opId nextReady -> RunStep1SaveFormOrder opId nextReady)
            (\st -> st { formPicker = st.formPicker { movedDownIdx = Nothing } })
            result

    Step1.ViewAction (Step1.ChangePage idx) ->
        startStep1Operation
            LoadRecentForms
            (\opId nextReady -> RunStep1PageChange opId nextReady idx)
            (\st -> st { selection = Sel.empty })
            result

    Step1.ViewAction (Step1.EditDocument { legacy_url: Just url }) ->
        appendCommand (OpenExternalUrl url) result

    Step1.ViewAction (Step1.EditDocument doc) ->
        applyStep1EditDocument doc result

    Step1.ViewAction Step1.EditDocuments ->
        applyStep1EditDocuments ready result

    Step1.ViewAction Step1.ToggleDocumentLocks -> case Step1.toggleLockRequests ready of
        Nothing ->
            result
        Just _ ->
            startStep1Operation
                (BulkToggleDocumentLocks $ Step1.selectedDocumentIds ready)
                (\opId nextReady -> RunStep1ToggleLocks opId nextReady)
                identity
                result

    Step1.ViewAction (Step1.ExportDocuments etype) ->
        startStep1Operation
            (ExportRecentForms $ Step1.selectedDocumentIds ready)
            (\opId nextReady -> RunStep1ExportDocuments opId nextReady etype)
            identity
            result

    Step1.ViewAction Step1.CreateForms ->
        applyStep1CreateForms ready result

    Step1.ViewAction Step1.CopyDocuments ->
        result

    Step1.ViewAction Step1.DeleteDocuments ->
        result

    Step1.ViewAction (Step1.RequestSignatures _) ->
        result

    Step1.ViewAction (Step1.UpdateForms _) ->
        result

    Step1.CopyStudentsSelected Nothing ->
        result

    Step1.CopyStudentsSelected (Just students) ->
        startStep1Operation
            (CopyRecentForms $ Step1.selectedDocumentIds ready)
            (\opId nextReady -> RunStep1CopyDocuments opId nextReady (Step1.copyRequests ready students))
            identity
            result

    Step1.DeleteConfirmed false ->
        result

    Step1.DeleteConfirmed true ->
        startStep1Operation
            (DeleteRecentForms $ Step1.selectedDocumentIds ready)
            (\opId nextReady -> RunStep1DeleteDocuments opId nextReady)
            identity
            result

    Step1.SignatureRequestsSelected Nothing ->
        result { state = setStep1Ready (ready { selection = Sel.empty }) result.state }

    Step1.SignatureRequestsSelected (Just requests) ->
        startStep1Operation
            (RequestRecentFormSignatures $ map _.docId requests)
            (\opId _ -> RunStep1RequestSignatures opId requests)
            identity
            result

    Step1.FormsUpdateSelected Nothing ->
        result

    Step1.FormsUpdateSelected (Just selection) ->
        startStep1Operation
            (UpdateRecentForms $ Step1.selectedDocumentIds ready)
            (\opId nextReady -> RunStep1UpdateForms opId nextReady selection)
            identity
            result

setStep1Ready :: Step1.RecentFormState -> AppState -> AppState
setStep1Ready ready state =
    -- Step 1 owns the visible recent-forms controls, but the runtime owns the
    -- search value used by route hydration and future loads.
    state
        { step1 = Step1Ready ready
        , workQueueUi = state.workQueueUi { search = ready.searchState }
        }

acceptStep1Message
    :: Step1.RecentFormState
    -> Step1.Msg
    -> UpdateResult
    -> { ready :: Step1.RecentFormState, result :: UpdateResult }
acceptStep1Message ready msg result
    | invalidatesStep1SearchDebounce msg =
        let nextReady = ready { searchVersion = ready.searchVersion + 1 }
        in
            { ready: nextReady
            , result: result { state = setStep1Ready nextReady result.state }
            }
    | otherwise =
        { ready, result }

invalidatesStep1SearchDebounce :: Step1.Msg -> Boolean
invalidatesStep1SearchDebounce = case _ of
    Step1.ViewAction (Step1.SearchChanged _) -> false
    _ -> true

type Step1MessagePolicy =
    { blockedByLoading :: Boolean
    , blockedByScopes :: Array OperationScope
    }

shouldBlockStep1Message :: Step1.RecentFormState -> Step1.Msg -> AppState -> Boolean
shouldBlockStep1Message ready msg state =
    (policy.blockedByLoading && isStep1Loading ready) ||
        Arr.any (OpStore.isScopeActive state.operations) policy.blockedByScopes
  where
    policy = step1MessagePolicy msg

isStep1Loading :: Step1.RecentFormState -> Boolean
isStep1Loading step1State = case step1State.state of
    Step1.RFLoading -> true
    _ -> false

step1MessagePolicy :: Step1.Msg -> Step1MessagePolicy
step1MessagePolicy msg
    | isStep1RecentFormsReloadMsg msg =
        { blockedByLoading: false
        , blockedByScopes: step1BlockingOperationScopes
        }
    | isStep1CreateFormsMsg msg =
        { blockedByLoading: false
        , blockedByScopes: step1BlockingOperationScopes
        }
    | isStep1DocumentMutationMsg msg =
        { blockedByLoading: true
        , blockedByScopes: [ Step1GlobalScope ]
        }
    | isStep1FormOrderMsg msg =
        { blockedByLoading: false
        , blockedByScopes: [ Step1GlobalScope ]
        }
    | isStep1GlobalActionMsg msg =
        { blockedByLoading: false
        , blockedByScopes: [ Step1GlobalScope ]
        }
    | otherwise =
        allowStep1Message

allowStep1Message :: Step1MessagePolicy
allowStep1Message =
    { blockedByLoading: false
    , blockedByScopes: []
    }

step1BlockingOperationScopes :: Array OperationScope
step1BlockingOperationScopes =
    [ Step1DocumentMutationScope
    , Step1FormOrderScope
    , Step1ExportScope
    ]

isStep1RecentFormsReloadMsg :: Step1.Msg -> Boolean
isStep1RecentFormsReloadMsg = case _ of
    Step1.ViewAction (Step1.SearchChanged _) -> true
    Step1.ViewAction (Step1.ChangePage _) -> true
    Step1.ViewAction (Step1.FormPickerChanged formPicker) -> not formPicker.reorder
    _ -> false

isStep1CreateFormsMsg :: Step1.Msg -> Boolean
isStep1CreateFormsMsg = case _ of
    Step1.ViewAction Step1.CreateForms -> true
    _ -> false

isStep1FormOrderMsg :: Step1.Msg -> Boolean
isStep1FormOrderMsg = case _ of
    Step1.ViewAction (Step1.FormPickerChanged formPicker) -> formPicker.reorder
    Step1.ViewAction Step1.StartFormReorder -> true
    Step1.ViewAction Step1.SaveFormOrder -> true
    _ -> false

isStep1DocumentMutationMsg :: Step1.Msg -> Boolean
isStep1DocumentMutationMsg = case _ of
    Step1.ViewAction Step1.ToggleDocumentLocks -> true
    Step1.ViewAction Step1.CopyDocuments -> true
    Step1.ViewAction Step1.DeleteDocuments -> true
    Step1.ViewAction (Step1.RequestSignatures _) -> true
    Step1.ViewAction (Step1.UpdateForms _) -> true
    Step1.CopyStudentsSelected (Just _) -> true
    Step1.DeleteConfirmed true -> true
    Step1.SignatureRequestsSelected (Just _) -> true
    Step1.FormsUpdateSelected (Just _) -> true
    _ -> false

isStep1GlobalActionMsg :: Step1.Msg -> Boolean
isStep1GlobalActionMsg = case _ of
    Step1.ViewAction _ -> true
    Step1.CopyStudentsSelected (Just _) -> true
    Step1.DeleteConfirmed true -> true
    Step1.SignatureRequestsSelected (Just _) -> true
    Step1.FormsUpdateSelected (Just _) -> true
    _ -> false

applyStep1EditDocument :: RecentForm -> UpdateResult -> UpdateResult
applyStep1EditDocument doc =
    applyStep1RuntimeNavigation Step3 \state ->
        state { workQueueState = editWorkQueue [ doc ] state.workQueueState }
  where
    editWorkQueue docs workQueue =
        setCurrentDoc (editDocsWorkQueue workQueue docs) $ Just
            { formId: doc.form_type.id
            , studentId: doc.student.id
            , documentId: Just doc.id
            }

applyStep1EditDocuments :: Step1.RecentFormState -> UpdateResult -> UpdateResult
applyStep1EditDocuments ready =
    applyStep1RuntimeNavigation Step3 \state ->
        state { workQueueState = setCurrentDoc (editDocsWorkQueue state.workQueueState selected) current }
  where
    selected = Step1.selectedForms ready
    current = map (\doc ->
        { formId: doc.form_type.id
        , studentId: doc.student.id
        , documentId: Just doc.id
        }) $ Arr.head selected

applyStep1CreateForms :: Step1.RecentFormState -> UpdateResult -> UpdateResult
applyStep1CreateForms ready =
    applyStep1RuntimeNavigation nextStep \state ->
        state
            { workQueueState = workQueueState { students = selectSelf workQueueCache.account.student }
            , workQueueUi = workQueueUi { search = ready.searchState }
            , workQueueCache = workQueueCache
            }
  where
    workQueueState = ready.formPicker.workQueueState
    workQueueUi = ready.formPicker.workQueueUi
    workQueueCache = ready.formPicker.workQueueCache
    isStudent = workQueueCache.account.type == StudentAccount
    formsSelected = not $ Sel.isEmpty workQueueState.forms
    teacherSelected = isJust workQueueState.teacher
    nextStep = if isStudent
        then (if formsSelected && teacherSelected then Step3 else Step2B)
        else Step2A
    selectSelf (Just student) | isStudent = Sel.select' student workQueueState.students
    selectSelf _ = workQueueState.students

applyStep1RuntimeNavigation :: Step -> (AppState -> AppState) -> UpdateResult -> UpdateResult
applyStep1RuntimeNavigation step prepare result =
    let state = supersedeLatestActiveInScope Step1RecentFormsScope result.state
        nextState = advanceRouteVersion $ activateStep step $ prepare state
    in
        { state: nextState
        , commands: result.commands <> [ SyncRoute PushRouteHistory ]
        }

startStep1Operation
    :: OperationKind
    -> (OperationId -> Step1.RecentFormState -> Command)
    -> (Step1.RecentFormState -> Step1.RecentFormState)
    -> UpdateResult
    -> UpdateResult
startStep1Operation kind mkCommand prepare result = case result.state.step1 of
    Step1Ready ready ->
        let nextState = applyStep1OperationStartPolicy kind result.state
            Tuple opId operations = OpStore.startOperation kind Nothing nextState.operations
            nextReady = Step1.startLoading $ prepare ready
        in result
            { state = setStep1Ready nextReady $ nextState { operations = operations }
            , commands = result.commands <> [ mkCommand opId nextReady ]
            }
    _ ->
        result

step1Error :: ErrorWrapper -> AppState -> UpdateResult
step1Error err state = case state.step1 of
    Step1Ready ready ->
        { state: setStep1Ready (Step1.setError err ready) state
        , commands: [ ReportError err ]
        }
    _ ->
        { state, commands: [ ReportError err ] }

isLatestOperation :: OperationScope -> OperationId -> AppState -> Boolean
isLatestOperation scope opId state = case OpStore.lookupLatestInScope state.operations scope of
    Just { id } -> id == opId
    Nothing -> false

isCurrentOperation :: OperationScope -> OperationId -> AppState -> Boolean
isCurrentOperation scope opId state =
    isLatestOperation scope opId state && OpStore.isOperationActive state.operations opId

applyStep1OperationStartPolicy :: OperationKind -> AppState -> AppState
applyStep1OperationStartPolicy kind state =
    Arr.foldl (\acc scope -> supersedeLatestActiveInScope scope acc) state $ supersededScopesForStep1Operation kind

supersededScopesForStep1Operation :: OperationKind -> Array OperationScope
supersededScopesForStep1Operation = case _ of
    LoadRecentForms -> [ Step1RecentFormsScope ]
    _ -> []

supersedeLatestActiveInScope :: OperationScope -> AppState -> AppState
supersedeLatestActiveInScope scope state = case OpStore.lookupLatestInScope state.operations scope of
    Just { id } | OpStore.isOperationActive state.operations id ->
        state { operations = OpStore.markSuperseded id state.operations }
    _ ->
        state

applyStep3Commands :: UpdateResult -> Array Step3Update.Command -> UpdateResult
applyStep3Commands = foldl applyStep3Command

applyStep3Command :: UpdateResult -> Step3Update.Command -> UpdateResult
applyStep3Command result = case _ of
    Step3Update.SyncRoute historyMode ->
        appendRouteCommand (SyncStep3Route historyMode) result

    Step3Update.StartSave generation -> case result.state.step3 of
        Step3Ready ready ->
            startStep3Operation
                (Step3Update.StartSave generation)
                (SaveStep3Document ready.editor.fillState.doc.id)
                generation
                (\opId -> SaveStep3 opId (SaveStep3Document ready.editor.fillState.doc.id) generation)
                result
        _ ->
            result

    Step3Update.StartLoad target -> case result.state.step3 of
        Step3Ready ready ->
            let prepared = Step3State.setPendingLoadTarget target ready
            in startStep3Operation
                (Step3Update.StartLoad target)
                (Step3State.loadOperationKindForState prepared)
                (Just target.generation)
                (\opId -> LoadStep3 opId (Step3State.loadOperationKindForState prepared) target)
                result
        _ ->
            result

    Step3Update.StartAutofill args -> case result.state.step3 of
        Step3Ready ready ->
            startStep3Operation
                (Step3Update.StartAutofill args)
                (AutofillStep3Document ready.editor.fillState.doc.id)
                Nothing
                (\opId -> AutofillStep3 opId (AutofillStep3Document ready.editor.fillState.doc.id) args)
                result
        _ ->
            result

    Step3Update.StartToggleLock -> case result.state.step3 of
        Step3Ready ready ->
            startStep3Operation
                Step3Update.StartToggleLock
                (ToggleStep3DocumentLock ready.editor.fillState.doc.id)
                Nothing
                (\opId -> ToggleStep3Lock opId (ToggleStep3DocumentLock ready.editor.fillState.doc.id))
                result
        _ ->
            result

    Step3Update.StartDocumentMutation request -> case result.state.step3 of
        Step3Ready ready ->
            let kind = step3DocumentMutationOperationKind ready.editor.fillState.doc.id request
            in startStep3Operation
                (Step3Update.StartDocumentMutation request)
                kind
                Nothing
                (\opId -> RunStep3DocumentMutation opId kind request)
                result
        _ ->
            result

    Step3Update.ConfirmCancel ->
        appendCommand ConfirmStep3Cancel result

    Step3Update.AllowPageUnload ->
        appendCommand AllowPageUnload result

    Step3Update.BlockPageUnload ->
        appendCommand BlockPageUnload result

    Step3Update.OpenExportUrl url ->
        appendCommand (OpenExportUrl url) result

    Step3Update.Navigate nav ->
        let currentState = result.state
                { workQueueState = step3WorkQueueState result.state
                , workQueueUi = step3WorkQueueUi result.state
                , workQueueCache = step3WorkQueueCache result.state
                }
            transition = applyRuntimeNavigation nav currentState
            nextState = if transition.shouldSyncRoute
                then advanceRouteVersion transition.state
                else transition.state
        in
            { state: nextState
            , commands: if transition.shouldSyncRoute then result.commands <> [ SyncRoute PushRouteHistory ] else result.commands
            }

    Step3Update.MarkSuperseded opId ->
        result { state = result.state { operations = OpStore.markSuperseded opId result.state.operations } }

step3DocumentMutationOperationKind :: DocumentID -> Step3Types.DocumentMutationRequest -> OperationKind
step3DocumentMutationOperationKind currentDocId = case _ of
    Step3Types.CreateComment docId _ ->
        CreateStep3Comment docId
    Step3Types.UpdateComment docId _ _ ->
        UpdateStep3Comment docId
    Step3Types.DeleteComment docId _ ->
        DeleteStep3Comment docId
    Step3Types.CreateAttachment docId _ ->
        CreateStep3Attachment docId
    Step3Types.DeleteAttachment docId _ ->
        DeleteStep3Attachment docId
    Step3Types.SignWithCapturedSignature docId _ _ _ ->
        SignStep3Document docId
    Step3Types.SignWithSavedSignature docId _ ->
        SignStep3DocumentWithSaved docId
    Step3Types.RefreshSignatureGroup docId ->
        RefreshStep3Signatures docId
    Step3Types.RequestSignatures requests ->
        RequestStep3Signatures $ requestDocIds requests
  where
    requestDocIds requests =
        let docIds = Arr.nub $ map _.docId requests
        in if Arr.null docIds then [ currentDocId ] else docIds

startStep3Operation
    :: Step3Update.Command
    -> OperationKind
    -> Maybe Int
    -> (OperationId -> Command)
    -> UpdateResult
    -> UpdateResult
startStep3Operation step3Command _kind _generation mkCommand result = case result.state.step3 of
    Step3Ready ready ->
        let Tuple opId operations = OpStore.reserveOperationId result.state.operations
            nextReady = Step3Update.applyStartedCommand ready step3Command opId
            nextState = (syncReadyState result.state nextReady) { operations = operations }
        in result
            { state = nextState
            , commands = result.commands <> [ mkCommand opId ]
            }
    _ ->
        result

appendCommand :: Command -> UpdateResult -> UpdateResult
appendCommand command result =
    result { commands = result.commands <> [ command ] }

appendRouteCommand :: Command -> UpdateResult -> UpdateResult
appendRouteCommand command result =
    appendCommand command $ result { state = advanceRouteVersion result.state }

advanceRouteVersion :: AppState -> AppState
advanceRouteVersion state = state { routeVersion = state.routeVersion + 1 }

syncReadyState :: AppState -> Step3State.Step3State -> AppState
syncReadyState state ready =
    state
        { workQueueState = ready.editor.workQueueState
        , workQueueUi = ready.editor.workQueueUi
        , workQueueCache = ready.editor.workQueueCache
        , step = Step3Doc ready.editor.fillState.doc.id
        , step3 = Step3Ready ready
        }

type RuntimeTransition =
    { state :: AppState
    , shouldSyncRoute :: Boolean
    }

applyRuntimeNavigation :: Navigation -> AppState -> RuntimeTransition
applyRuntimeNavigation nav state = case nav of
    NavNext ->
        { state: finishStepNavigation state
        , shouldSyncRoute: true
        }

    NavCancel ->
        { state: exitToHome state
        , shouldSyncRoute: true
        }

    NavJump step ->
        { state: activateStep step state
        , shouldSyncRoute: true
        }

finishStepNavigation :: AppState -> AppState
finishStepNavigation state = case state.step of
    Step2A ->
        activateStep (if Map.isEmpty state.workQueueState.forms.sel then Step2B else Step3) state

    Step2B ->
        activateStep (if Sel.isEmpty state.workQueueState.students then Step2A else Step3) state

    Step3 ->
        exitToHome state

    Step3Doc _ ->
        exitToHome state

    _ ->
        state

exitToHome :: AppState -> AppState
exitToHome state =
    activateStep Step1 $ state
        { workQueueState = resetWorkQueueState defTeacher defSemester state.workQueueState
        , workQueueUi = resetWorkQueueUiState state.workQueueCache.defTeacher state.workQueueCache.defSemester state.workQueueUi
        , preselectForms = Arr.nub $ editedDocumentIds <> createdDocumentIds
        }
    where
        defTeacher = map _.value state.workQueueCache.defTeacher
        defSemester = map _.value state.workQueueCache.defSemester
        editedDocumentIds = map _.id state.workQueueState.editDocs
        createdDocumentIds = map _.id $ Arr.fromFoldable $ listDocs state.workQueueState.documents

step3WorkQueueState :: AppState -> WorkQueueState
step3WorkQueueState state = case state.step3 of
    Step3Ready ready -> ready.editor.workQueueState
    _ -> state.workQueueState

step3WorkQueueUi :: AppState -> WorkQueueUiState
step3WorkQueueUi state = case state.step3 of
    Step3Ready ready -> ready.editor.workQueueUi
    _ -> state.workQueueUi

step3WorkQueueCache :: AppState -> WorkQueueCache
step3WorkQueueCache state = case state.step3 of
    Step3Ready ready -> ready.editor.workQueueCache
    _ -> state.workQueueCache

currentRouteForState :: AppState -> LocationState -> AppRoute.Route
currentRouteForState state locationState = case state.step3 of
    Step3Ready ready ->
        AppRoute.RStep3
            { preservedQuery: AppRoute.preserveQuery locationState.search
            , activeDoc: Just ready.editor.fillState.doc.id
            , docs: Arr.nub $ step3DocIds ready
            , section: ready.editor.sectionIntent
            }

    Step3LoadingFormTypes _ -> loadingRouteFallback

    Step3LoadingInitialState _ -> loadingRouteFallback

    _ ->
        AppRoute.routeForStepAndWorkQueueState locationState state.step state.workQueueState
  where
    loadingRouteFallback = case state.step of
        Step3 -> loadingStep3Route locationState state.workQueueState
        _ -> AppRoute.routeForStepAndWorkQueueState locationState state.step state.workQueueState

-- | Handle route changes for Step3 forms.
handleReadyStep3BrowserRoute :: AppState -> AppRoute.Route -> Either ErrorWrapper (Maybe UpdateResult)
handleReadyStep3BrowserRoute state requested = case state.step3, requested of
    Step3Ready ready, AppRoute.RStep3 { activeDoc: Just docId, docs, section } ->
        let
            requestedDocIds = Arr.nub $ Arr.cons docId docs
        in if step3RequestedDocIdsMatch ready requestedDocIds
            then handleKnownRoute false
            else if Arr.all (\docId' -> docId' `Arr.elem` step3DocIds ready) requestedDocIds
                then handleKnownRoute true
                else pure Nothing
      where
        sectionAnchor :: AppRoute.SectionIntent -> Maybe String
        sectionAnchor = case _ of
            AppRoute.SectionSet section' -> Just section'
            _ -> Nothing

        maybeScrollStep3Commands :: AppRoute.SectionIntent -> Array Command
        maybeScrollStep3Commands = case _ of
            AppRoute.SectionSet section' -> [ ScrollStep3Section section' ]
            _ -> []

        normaliseStep3BrowserSection :: AppRoute.SectionIntent -> AppRoute.SectionIntent
        normaliseStep3BrowserSection = case _ of
            AppRoute.SectionSet section' -> AppRoute.SectionSet section'
            _ -> AppRoute.SectionClear

        handleKnownRoute forceRouteReplace =
            if docId == ready.editor.fillState.doc.id
                then
                    let nextSection = normaliseStep3BrowserSection section
                        nextReady = ready
                            { editor = ready.editor
                                { anchor = Nothing
                                , activeSection = sectionAnchor nextSection
                                , sectionIntent = nextSection
                                }
                            }
                        routeCommands = if forceRouteReplace then [ SyncStep3Route ReplaceStep3History ] else []
                    -- Check whether the hash part matches in both current and
                    -- requested URLs; if so, there's no need to scroll.
                    in if AppRoute.sectionHash (sectionAnchor nextSection) == AppRoute.sectionHash (sectionAnchor ready.editor.sectionIntent)
                        then pure $ Just { state, commands: routeCommands }
                        else pure $ Just
                            { state: syncReadyState state nextReady
                            , commands: maybeScrollStep3Commands nextSection <> routeCommands
                            }
                else case step3DocRouteIndex ready docId of
                    Just idx -> do
                        step3Result <- browserRouteStep3Result <$> Step3Update.update ready
                            (Step3Update.ViewEvent $ Step3Types.JumpIdxF idx (sectionAnchor $ normaliseStep3BrowserSection section))
                        pure $ Just $
                            applyStep3Commands
                                { state: syncReadyState state step3Result.state
                                , commands: []
                                }
                                step3Result.commands
                    Nothing ->
                        pure Nothing
    _, _ ->
        pure Nothing

step3DocRouteIndex :: Step3State.Step3State -> DocumentID -> Maybe Int
step3DocRouteIndex ready docId =
    Arr.findIndex matchesDoc ready.editor.queue
  where
    matchesDoc entry = case entry.form of
        Left recentForm -> recentForm.id == docId
        Right formId -> case getDocByStudent ready.editor.workQueueState { studentId: entry.student, formId, documentId: Nothing } of
            Just doc -> doc.id == docId
            Nothing -> false

step3RequestedDocIdsMatch :: Step3State.Step3State -> Array DocumentID -> Boolean
step3RequestedDocIdsMatch ready requestedDocIds =
    Arr.length dedupedRequested == Arr.length expectedDocIds
        && Arr.all (\docId -> docId `Arr.elem` expectedDocIds) dedupedRequested
  where
    dedupedRequested = Arr.nub requestedDocIds
    expectedDocIds = step3DocIds ready

browserRouteStep3Result
    :: { state :: Step3State.Step3State, commands :: Array Step3Update.Command }
    -> { state :: Step3State.Step3State, commands :: Array Step3Update.Command }
browserRouteStep3Result result =
    { state: Step3State.mapEditorState updateTransitionState result.state
    , commands: map updateCommand result.commands
    }
  where
    updateTransitionState editor = editor
        { transitionState = editor.transitionState
            { latest = map (\pending -> pending { historyMode = ReplaceStep3History }) editor.transitionState.latest
            }
        }

    updateCommand = case _ of
        Step3Update.SyncRoute _ -> Step3Update.SyncRoute ReplaceStep3History
        command -> command

loadingStep3Route :: LocationState -> WorkQueueState -> AppRoute.Route
loadingStep3Route locationState workQueue =
    AppRoute.RStep3
        { preservedQuery: AppRoute.preserveQuery locationState.search
        , activeDoc: Nothing
        , docs: Arr.nub $ step3WorkQueueDocIds workQueue
        , section: AppRoute.SectionClear
        }

step3WorkQueueDocIds :: WorkQueueState -> Array DocumentID
step3WorkQueueDocIds workQueue =
    Arr.nub $ (map _.id workQueue.editDocs) <> (map _.id $ Arr.fromFoldable $ listDocs workQueue.documents)

step3DocIds :: Step3State.Step3State -> Array DocumentID
step3DocIds ready =
    Arr.nub $ (map _.id ready.editor.workQueueState.editDocs) <> (map _.id $ Arr.fromFoldable $ listDocs ready.editor.workQueueState.documents)
